// Copyright (c) StrangeBee 2024

import React, { ReactElement, useCallback, useContext, useMemo, useState } from 'react';
import {
    CaseOutput,
    ObjectTypeV1 as ObjectType,
    TaskStatus,
    CaseStatusStage,
    CustomStatusMapType,
    PermissionsEnum,
} from 'thehive-sdk';
import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import CaseListCheckboxCell from '@Organisms/Case/CaseListCheckboxCell/CaseListCheckboxCell';
import { useCurrentUser } from '@Queries/userQueries';
import { classList, convertToLocaleId, handleNotificationError, openMessage, useLocalMessage } from '@Helpers/';
import CaseStats from '@Organisms/Case/CaseStats/CaseStats';
import useConfigList from '@Hooks/useConfigList';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import client from '@Client';
import RouteBuilder from '@Services/RouteBuilder';
import { Link, useParams } from 'react-router-dom';
import { Badge, Space } from 'antd';
import { RouteType } from '@Types';
import { ActionRequiredButton } from '@Atoms/Info';
import { HTMLColors, TaskStatusColors } from '@Constants/colors';
import FormatMultipleDate from '@Molecules/Format/FormatMultipleDate/FormatMultipleDate';
import { THList } from '@Molecules/THList';
import ActionList from '@Molecules/ActionList/ActionList';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';
import { ManageSwitch } from '@Molecules/ManageSwitch/ManageSwitch';
import { THFilter } from '@Molecules/THFilter/THFilter';
import { useListAllFields } from '@Queries/describeQueries';
import QuickFilterList from '@Molecules/QuickFilterList/QuickFilterList';
import CaseListTitle from '@Organisms/Case/CaseListTitle/CaseListTitle';
import { SelectedInfo } from '@Atoms/SelectedInfo/SelectedInfo';
import { Footer } from '@Organisms/ExtendFilterFooter/ExtendFilterFooter';
import { ICONS } from '@Constants/iconsApp';
import { computeEventList } from '@Organisms/Case/eventList';
import { FormatDate } from '@Molecules/Format';
import { PaginationBrowse } from '@Atoms/Inputs/PaginationBrowse/PaginationBrowse';
import { MultipleDisplayEntityInfo } from '@Molecules/MultipleDisplayEntityInfo/MultipleDisplayEntityInfo';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import useDisplayParams from '@Molecules/DisplayParams/DisplayParams';
import { useEffect } from 'react';
import { ColorLabel } from '@Atoms/Info/ColorLabel/ColorLabel';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import { QuickFiltersAvailable } from '@Hooks/useQuickFilters';
import { ManageViewList } from '@Molecules/ManageViewList/ManageViewList';
import { useDrawer } from '@Hooks/useDrawer';
import { CaseDrawer } from '@Organisms/Case/CaseDrawer/CaseDrawer';
import { ScopeContext } from '@Context/ScopeProvider';
import { actionList } from '@Organisms/Case/actionList';
import { computeListSelector } from '@Organisms/Case/selector';
import { useGenericQuery } from '@Queries/genericQueries';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { useSplitPermissions } from '@Hooks/useSplitPermissions';
import { ExportButton } from '@Organisms/Export/ExportDrawer/ExportButton';
import usePageTitle from '@Hooks/usePageTitle';

import './CaseList.scss';

const CaseList = (): ReactElement => {
    const i = useLocalMessage();

    const { viewId } = useParams<{ viewId?: string }>();

    const { visible, setVisible, data, setData } = useDrawer<CaseOutput>({});

    const { data: currentUser } = useCurrentUser();
    const [caseStatus, setCaseStatus] = React.useState<CustomStatusMapType>({});

    const { login = '', organisations = [], organisation: userOrganisation = '', permissions = [] } = currentUser || {};

    const links = organisations.find(({ organisation }) => organisation === userOrganisation)?.links || [];
    const orgHasLinks = !!links.length;

    const {
        customStatus: {
            case: { list: customStatusList },
        },
    } = useContext(ScopeContext);

    const { data: statusUser = [], refetch: refetchStatusUser } = useGenericQuery({
        fetchCallback: () =>
            client.ChartController.getChartCategory({
                model: 'Case',
                field: 'stage',
                aggs: [{ _agg: 'count', _name: 'count' }],
            }),
        key: ['get-count-status-user'],
        fetchOnStart: true,
        manual: true,
    });

    const config = useConfigList<CaseOutput>({
        selector: computeListSelector(),
        controller: client.CaseController,
        storageId: StorageFilterEnum.CASE,
        objectType: ObjectType.CASE,
        defaultView: viewId,
    });

    const {
        storagePageManager,
        storageViewManager,
        stateNeedRefresh: { needRefresh, setNeedRefresh },
        listRefetch,
        countRefetch,
        status = 'loading',
        dataSource = [],
        columnsRenderer,
        quickFilterListRenderer,
        rowSelectionCustomRenderer,
        paginationManager,
        table,
        stateSelectedKeys: { selectedKeys, setSelectedKeys },
        filterManager,
        quickFilters,
        queryParams,
    } = config;

    useDrawer.watch(dataSource, () => {
        if (data) {
            setData(dataSource.find(({ _id }) => _id === data._id));
        }
    });

    const { setDefaultDisplay } = storageViewManager || {};

    const {
        page: storagePage,
        setDisplay,
        setStatsIsOpen,
        setFilterIsOpen,
        setAutoRefresh,
        setOnLoadView,
    } = storagePageManager || { page: null };

    /** CUSTOM DISPLAY TABLE */
    const displayDetailsConfig: Record<string, any> = {
        tasks: { label: i('common.tasks'), visible: storagePage?.display?.tasks ?? true },
        observables: { label: i('common.observables'), visible: storagePage?.display?.observables ?? true },
        ttp: { label: i('common.ttp'), visible: storagePage?.display?.ttp ?? true },
        alerts: { label: i('common.linked-alerts'), visible: storagePage?.display?.alerts ?? true },
    };

    if (orgHasLinks) {
        displayDetailsConfig['shares'] = { label: i('common.shares'), visible: storagePage?.display?.shares ?? true };
    }

    const [showDetails, DisplayDetails, loadVisibleStateDetails] = useDisplayParams(displayDetailsConfig);

    const [showDates, DisplayDates, loadVisibleStateDates] = useDisplayParams({
        startDate: { label: i('common.start-date'), visible: storagePage?.display?.startDate ?? true },
        createdAt: { label: i('common.created-date'), visible: storagePage?.display?.createdAt ?? true },
        updatedAt: { label: i('common.updated-date'), visible: storagePage?.display?.updatedAt ?? true },
    });

    const [showTitle, DisplayTitle, loadVisibleStateTitle] = useDisplayParams({
        tags: { label: i('common.tags'), visible: storagePage?.display?.tags ?? true },
        customFields: { label: i('common.custom-fields'), visible: storagePage?.display?.customFields ?? true },
    });

    const [localAutoRefresh, setLocalAutoRefresh] = useState(storagePage?.autoRefresh || false);
    const [localStatsIsOpen, setLocalStatsIsOpen] = useState(storagePage?.statsIsOpen || false);
    const [localFilterIsOpen, setLocalFilterIsOpen] = useState(storagePage?.filterIsOpen || false);
    const [poolStatusStarted, setPoolStatusStarted] = useState(false);
    const [exportDrawerVisible, setExportDrawerVisible] = useState(false);

    setOnLoadView?.((viewContent) => {
        setLocalAutoRefresh(viewContent?.autoRefresh || false);
        setLocalStatsIsOpen(viewContent?.statsIsOpen || false);

        loadVisibleStateDetails({
            tasks: viewContent?.display?.tasks ?? true,
            observables: viewContent?.display?.observables ?? true,
            ttp: viewContent?.display?.ttp ?? true,
            alerts: viewContent?.display?.alerts ?? true,
            ...(orgHasLinks ? { shares: storagePage?.display?.shares ?? true } : {}),
        });
        loadVisibleStateDates({
            startDate: viewContent?.display?.startDate ?? true,
            createdAt: viewContent?.display?.createdAt ?? true,
            updatedAt: viewContent?.display?.updatedAt ?? true,
        });
        loadVisibleStateTitle({
            tags: viewContent?.display?.tags ?? true,
            customFields: viewContent?.display?.customFields ?? true,
        });
    });

    const { data: describe } = useListAllFields();

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.CaseController,
                locale: i,
                onError: handleNotificationError,
                onSuccess: (count, message) => {
                    if (count) {
                        countRefetch();
                    }
                    if (message) {
                        openMessage('success', message);
                    }
                    poolStatusStarted || listRefetch();
                    refetchStatusUser();
                },
            }),
        [listRefetch, countRefetch, i, poolStatusStarted, refetchStatusUser],
    );

    const openCaseCount = useMemo(() => {
        if (statusUser) {
            return statusUser.reduce<number>((acc, { _key, count }) => {
                if (_key !== CaseStatusStage.CLOSED) {
                    return acc + count;
                }
                return acc;
            }, 0);
        }
        return 0;
    }, [statusUser]);

    const configQuickerFilter = useMemo(
        () => [
            {
                title: { label: i('filter.quick.case.open'), count: openCaseCount },
                onClick: ({ custom }: QuickFiltersAvailable): void => {
                    custom({ field: 'stage', operator: 'not', values: [CaseStatusStage.CLOSED] });
                },
            },
            {
                title: {
                    label: i('filter.quick.case.closed'),
                    count: statusUser?.find(({ _key }) => _key === CaseStatusStage.CLOSED)?.count || 0,
                },
                onClick: ({ custom }: QuickFiltersAvailable): void => {
                    custom({ field: 'stage', operator: 'any', values: [CaseStatusStage.CLOSED] });
                },
            },
            { divider: true },
            {
                title: i('filter.quick.case.my-open'),
                onClick: ({ assignee, custom }: QuickFiltersAvailable): void => {
                    assignee(login);
                    custom({ field: 'stage', operator: 'not', values: [CaseStatusStage.CLOSED] });
                },
            },
            {
                title: i('filter.quick.case.my-closed'),
                onClick: ({ assignee, custom }: QuickFiltersAvailable): void => {
                    assignee(login);
                    custom({ field: 'stage', operator: 'any', values: [CaseStatusStage.CLOSED] });
                },
            },
            { divider: true },
            {
                title: i('filter.quick.case.owned'),
                onClick: ({ custom: filterCustom }: QuickFiltersAvailable): void => {
                    filterCustom({
                        field: 'owningOrganisation',
                        operator: 'any',
                        values: [currentUser?.organisation],
                    });
                },
            },
            {
                title: i('filter.quick.case.shared'),
                onClick: ({ custom: filterCustom }: QuickFiltersAvailable): void => {
                    filterCustom({
                        field: 'owningOrganisation',
                        operator: 'not',
                        values: [currentUser?.organisation],
                    });
                },
            },
            { divider: true },
            {
                title: i('filter.quick.case.unassigned'),
                onClick: ({ assignee }): void => assignee(),
            },
        ],
        [i, currentUser, login, openCaseCount, statusUser],
    );
    const quickFilterList = quickFilterListRenderer(configQuickerFilter);

    const rowSelection = rowSelectionCustomRenderer({
        label: i('common.selected'),
        render: (data, checkbox, { flag: filterFlag }): ReactElement => (
            <CaseListCheckboxCell
                data={data}
                checkbox={checkbox}
                orgHasLinks={orgHasLinks}
                onFlagClick={(value) => filterFlag(value)}
            />
        ),
    });

    const onStartPoolStatusManage = useCallback(() => {
        setPoolStatusStarted(true);
        setLocalAutoRefresh(false);
    }, []);

    const onFinishPoolStatusManage = useCallback(() => {
        setPoolStatusStarted(false);
        setLocalAutoRefresh(storagePage?.autoRefresh || false);
    }, [storagePage]);

    const hasPermissions = useSplitPermissions(permissions, [
        PermissionsEnum.MANAGE_CASE_UPDATE,
        PermissionsEnum.MANAGE_CASE_DELETE,
        PermissionsEnum.MANAGE_ACTION,
        PermissionsEnum.MANAGE_CASE_REOPEN,
    ]);

    const caseActionList = actionList({
        local: i,
        caseStatus,
        eventList,
        customStatusList,
        organisation: userOrganisation,
        onStartPoolStatusManage,
        onFinishPoolStatusManage,
        onCloseBulkStatusManage: () => {
            listRefetch();
            refetchStatusUser();
            setSelectedKeys([]);
        },
        onEditBulkReopen: () => {
            listRefetch();
            setSelectedKeys([]);
        },
        onDeleteBulk: () => {
            listRefetch();
            countRefetch();
            setSelectedKeys([]);
        },
        hasPermissions,
        currentUserData: currentUser,
    });

    const columns = columnsRenderer([
        {
            title: i('case.list.table.columns.status'),
            className: 'case-list__column__status',
            render: (
                { extraData = {}, startDate = 0, endDate = 0, _createdAt = 0 },
                { status: filterStatus },
            ): ReactElement => (
                <div
                    className={classList('case-list__status', {
                        open: extraData.status?.stage !== CaseStatusStage.CLOSED,
                        closed: extraData.status?.stage === CaseStatusStage.CLOSED,
                    })}
                    style={{ [`--color-status` as string]: extraData.status?.colour || HTMLColors.DARK_GRAY }}
                >
                    <ColorLabel
                        label={i({
                            id: `case.data.status.${convertToLocaleId(extraData.status?.value || '')}`,
                            defaultMessage: extraData.status?.value,
                        })}
                        color={extraData.status?.colour || HTMLColors.DARK_GRAY}
                        className={classList('status-tag')}
                        type="fill"
                        onClick={(): void | null => (extraData.status ? filterStatus(extraData.status.value) : null)}
                    />

                    <div className="case-list__status__date">
                        <THTooltip
                            title={
                                extraData.status?.stage !== CaseStatusStage.CLOSED
                                    ? i('common.open-since')
                                    : i('common.closed-after')
                            }
                            placement="bottom"
                        >
                            <ICONS.ClockCircleOutlined />
                            {extraData.status?.stage !== CaseStatusStage.CLOSED ? (
                                <FormatDate date={_createdAt} onlyFrom withoutSuffix />
                            ) : (
                                <FormatDate date={startDate} endDate={endDate} onlyFrom withoutSuffix />
                            )}
                        </THTooltip>
                    </div>
                </div>
            ),
        },
        {
            title: [
                { label: i('case.list.table.columns.severity'), sortField: 'severity' },
                { label: i('case.list.table.columns.case-number'), sortField: 'number' },
                { label: i('case.list.table.columns.title'), sortField: 'title' },
            ],
            className: 'case-list__column-title column_preview',
            optionalRender: DisplayTitle,
            render: (
                data,
                { severity: filterSeverity, tags: filterTags, customField: filterCustomField },
            ): ReactElement => (
                <CaseListTitle
                    data={data}
                    onSeverityClick={filterSeverity}
                    onTagClick={(tagValue, event): void => {
                        const isAltPressed = event.altKey;
                        filterTags([tagValue], isAltPressed);
                    }}
                    customFieldsIsOpen={showTitle?.customFields}
                    tagsIsOpen={showTitle?.tags}
                    onCustomFieldsClick={(customField): void => filterCustomField(customField)}
                    onPreviewClick={() => {
                        setData(data);
                        setVisible(true);
                    }}
                />
            ),
        },
        {
            title: i('case.list.table.columns.details'),
            className: 'case-list__column-details',
            optionalRender: DisplayDetails,
            render: (
                {
                    _id: caseId,
                    extraData: {
                        observableStats: { total: observableCount = 0 },
                        taskStats: { total: taskCount = 0, ...taskStats },
                        alerts,
                        procedureCount = 0,
                        actionRequired = false,
                        shareCount = 0,
                    },
                },
                { custom: filterCustom },
            ): ReactElement => (
                <div className="">
                    <MultipleDisplayEntityInfo
                        className="case-list__details"
                        data={[
                            {
                                label: (
                                    <Space size="small">
                                        {actionRequired && (
                                            <ActionRequiredButton
                                                onClick={(event): void => {
                                                    event.stopPropagation();
                                                    filterCustom({ field: 'actionRequired', operator: 'true' });
                                                }}
                                            />
                                        )}
                                        {i('common.tasks')}
                                    </Space>
                                ),
                                danger: actionRequired,
                                count: taskCount,
                                hide: !showDetails.tasks,
                                stats: taskCount
                                    ? Object.entries(taskStats)
                                          .sort(([a], [b]) => b.localeCompare(a))
                                          .map(([status, count]) => ({
                                              count,
                                              color: TaskStatusColors[status as TaskStatus],
                                              description: status,
                                          }))
                                    : undefined,
                                link: RouteBuilder.compute('cases.tasks', { caseId }),
                            },
                            {
                                label: i('common.observables'),
                                count: observableCount,
                                hide: !showDetails.observables,
                                link: RouteBuilder.compute('cases.observables', { caseId }),
                            },
                            {
                                label: i('common.ttp'),
                                count: procedureCount,
                                hide: !showDetails.ttp,
                                link: RouteBuilder.compute('cases.procedures', { caseId }),
                            },
                            {
                                label: i('common.linked-alerts'),
                                count: alerts.length,
                                hide: !showDetails.alerts,
                                link: RouteBuilder.compute('cases.linked-alerts', { caseId }),
                            },
                            {
                                label: i('common.shares'),
                                count: shareCount,
                                hide: !showDetails.shares,
                            },
                        ]}
                    />
                </div>
            ),
        },
        {
            title: [{ label: i('case.list.table.columns.assignee'), sortField: 'assignee' }],
            className: 'column-assigneeProps',
            render: ({ assignee }, { assignee: filterAssignee }): ReactElement => (
                <AssigneeInfo onClick={filterAssignee} assignee={assignee} avatarOnly />
            ),
        },
        {
            title: [
                { label: i('common.dates') },
                { label: 'S.', sortField: 'startDate', hide: !showDates.startDate },
                { label: 'C.', sortField: '_createdAt', hide: !showDates.createdAt },
                { label: 'U.', sortField: '_updatedAt', hide: !showDates.updatedAt },
            ],
            optionalRender: DisplayDates,
            className: 'case-list__column-date',
            render: ({ _createdAt = 0, startDate = 0, _updatedAt = 0 }, { date: filterDate }): ReactElement => (
                <FormatMultipleDate
                    dates={[
                        {
                            label: 'S.',
                            value: startDate,
                            onClick: (): void => filterDate(startDate, 'startDate'),
                            hide: !showDates.startDate,
                        },
                        {
                            label: 'C.',
                            value: _createdAt,
                            onClick: (): void => filterDate(_createdAt, '_createdAt'),
                            hide: !showDates.createdAt,
                        },
                        {
                            label: 'U.',
                            value: _updatedAt,
                            onClick: (): void => filterDate(_updatedAt, '_updatedAt'),
                            hide: !showDates.updatedAt,
                        },
                    ]}
                />
            ),
        },
        {
            className: 'observable-list__column-actions',
            render: (data): ReactElement | null =>
                Object.values(hasPermissions).some(Boolean) ? (
                    <ActionsList
                        actionList={caseActionList([data], (count) => {
                            listRefetch?.();
                            if (count) {
                                countRefetch?.();
                            }
                        })}
                        scope="single"
                        isIcon
                    />
                ) : null,
        },
    ]);

    useEffect(() => {
        setDisplay?.('tags', showTitle.tags);
        setDisplay?.('customFields', showTitle.customFields);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showTitle]);

    useEffect(() => {
        setDisplay?.('tasks', showDetails.tasks);
        setDisplay?.('observables', showDetails.observables);
        setDisplay?.('ttp', showDetails.ttp);
        setDisplay?.('alerts', showDetails.alerts);
        setDisplay?.('shares', showDetails.shares);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showDetails]);

    useEffect(() => {
        setDisplay?.('startDate', showDates.startDate);
        setDisplay?.('createdAt', showDates.createdAt);
        setDisplay?.('updatedAt', showDates.updatedAt);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showDates]);

    useEffect(() => {
        const currentView = storagePage?.currentView || '';

        setDefaultDisplay?.(currentView, {
            tags: showTitle.tags,
            customFields: showTitle.customFields,
            tasks: showDetails.tasks,
            observables: showDetails.observables,
            ttp: showDetails.ttp,
            alerts: showDetails.alerts,
            shares: showDetails.shares,
            startDate: showDates.startDate,
            createdAt: showDates.createdAt,
            updatedAt: showDates.updatedAt,
        });
    }, [storagePage, showTitle, showDetails, showDates, setDefaultDisplay]);

    useEffect(() => {
        setAutoRefresh?.(localAutoRefresh);
    }, [localAutoRefresh]); // eslint-disable-line

    useEffect(() => {
        setStatsIsOpen?.(localStatsIsOpen);
    }, [localStatsIsOpen]); // eslint-disable-line

    const setLocalFilters = (localFilters: boolean): void => {
        setLocalFilterIsOpen(localFilters);
        setFilterIsOpen?.(localFilters);
        filterManager.setEditFilters(localFilters);
    };

    useEffect(() => {
        setLocalFilterIsOpen(filterManager.editFilters);
    }, [filterManager.editFilters]); // eslint-disable-line

    useEffect(() => {
        if (customStatusList.length) {
            const caseStatusMap = customStatusList.reduce((acc, curr) => {
                if (![CaseStatusStage.NEW, CaseStatusStage.IN_PROGRESS].includes(curr.stage as CaseStatusStage)) {
                    return acc;
                }
                if (!acc[curr.stage]) {
                    acc[curr.stage] = [];
                }
                acc[curr.stage].push(curr);
                return acc;
            }, {} as CustomStatusMapType);
            setCaseStatus(caseStatusMap);
        }
    }, [customStatusList]);

    usePageTitle(
        i('page-title.cases.list', {
            count: dataSource.filter((item) => item.status === CaseStatusStage.NEW).length || 0,
        }),
    );

    return (
        <>
            <THList>
                <THList.Header>
                    <THList.Slot name="MANAGE">
                        {storagePageManager && storageViewManager ? (
                            <ManageViewList
                                pageManager={storagePageManager}
                                viewManager={storageViewManager}
                                beforeCreate={(content: any): any => {
                                    filterManager.applyFilters();
                                    return { ...content, filters: filterManager.tempFilters };
                                }}
                            />
                        ) : null}

                        <QuickFilterList quickFilterList={quickFilterList} />

                        <ExportButton
                            onClick={() => {
                                setExportDrawerVisible(true);
                            }}
                            objectType="Case"
                            selectedItems={dataSource.reduce((acc: string[], data) => {
                                if (selectedKeys.includes(data['_id'])) {
                                    acc.push(data['_id']);
                                }
                                return acc;
                            }, [])}
                            queryParams={queryParams}
                            visible={exportDrawerVisible}
                            afterVisible={setExportDrawerVisible}
                            listNumber={table.count}
                        />

                        <SelectedInfo count={selectedKeys?.length} label={i('common.selected')} />

                        <ActionList
                            actions={caseActionList(
                                dataSource.filter((data: { _id: string }) => selectedKeys.includes(data['_id'])),
                                (count) => {
                                    listRefetch?.();
                                    table?.setSelectedKeys?.([]);
                                    if (count) {
                                        countRefetch?.();
                                    }
                                },
                            )}
                            scope="list"
                            hidden={!selectedKeys?.length}
                        />
                    </THList.Slot>
                    <THList.Slot name="NAVIGATION">
                        {!storagePage?.autoRefresh && (
                            <Badge dot={needRefresh}>
                                <THTooltip
                                    title={needRefresh ? i('common.refresh') : i('common.update-list')}
                                    placement="bottom"
                                >
                                    <THButton
                                        size="small"
                                        type="default"
                                        shape="circle"
                                        onClick={() => {
                                            listRefetch();
                                            countRefetch();
                                            setNeedRefresh(false);
                                        }}
                                    >
                                        <ICONS.SyncOutlined />
                                    </THButton>
                                </THTooltip>
                            </Badge>
                        )}
                        <ManageSwitch
                            label={i('common.auto-refresh')}
                            value={localAutoRefresh}
                            onChange={(autoRefresh): void => setLocalAutoRefresh(autoRefresh)}
                            icon={<ICONS.SyncOutlined />}
                        />
                        <ManageSwitch
                            label={i('common.stats')}
                            value={localStatsIsOpen}
                            onChange={(stats): void => setLocalStatsIsOpen(stats)}
                            icon={<ICONS.PieChartOutlined />}
                        />
                        <ManageSwitch
                            label={i('common.filters')}
                            value={localFilterIsOpen}
                            onChange={(editFilters): void => setLocalFilters(editFilters)}
                            icon={<ICONS.FilterOutlined />}
                        />
                    </THList.Slot>
                    <THList.Slot name="CONTENT">
                        {localStatsIsOpen && <CaseStats quickFilters={quickFilters} />}
                        {(localFilterIsOpen || !!filterManager.tempFilters.length) && (
                            <THFilter filterConfig={filterManager} describeField={describe?.[ObjectType.CASE]}>
                                <Footer filterConfig={filterManager} />
                            </THFilter>
                        )}
                    </THList.Slot>
                </THList.Header>

                <THList.Content
                    dataSource={dataSource}
                    columns={columns}
                    rowKey="_id"
                    rowSelection={hasPermissions ? rowSelection : undefined}
                    emptyPlaceholder={<DisplayEmptyData spin={status === 'loading'} label={i('case.none')} />}
                />

                <THList.Footer>
                    <THList.Slot name="NAVIGATION">
                        <PaginationBrowse count={table.count} paginationConfig={paginationManager} />
                        <PaginationSize paginationConfig={paginationManager} />
                    </THList.Slot>
                </THList.Footer>
            </THList>

            {data && visible && (
                <CaseDrawer
                    data={data}
                    eventList={eventList}
                    actionList={caseActionList}
                    visible={visible}
                    afterVisible={setVisible}
                    onEditCounter={() => listRefetch?.()}
                />
            )}
        </>
    );
};
const path = RouteBuilder.compute('cases.default');
const MenuLink = (): ReactElement => {
    const local = useLocalMessage();
    return <Link to={path}>{local('mainLayout.userMenu.cases')}</Link>;
};

export const Route: RouteType = {
    path,
    menu: {
        key: 'cases',
        icon: <ICONS.CaseIcon />,
        render: <MenuLink />,
        selectTab: path,
    },
    breadcrumb: { label: 'breadcrumb.global.cases' },
    content: <CaseList />,
    space: ['user'],
    exact: true,
};

export const RouteView: RouteType = {
    path: RouteBuilder.compute('cases.views'),
    content: <CaseList />,
    space: ['user'],
    exact: true,
};
